import * as React from 'react';
import {
  Radio as ShadcnRadio,
  RadioItem as ShadcnRadioItem,
} from '@/hen-ui/shadcn/radio';
import { Label } from '@/hen-ui/atomics/Label';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';
export interface RadioProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnRadio> {
  options: {
    value: string;
    label: string;
    disabled?: boolean;
    error?: boolean;
  }[];
}

/**
 * Radio component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui radio (Radix UI Radio primitive) with HEN-specific
 * variants and design tokens. All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage - horizontal orientation by default
  <Radio
    options={[
      {
        label: 'Option 1',
        value: 'option1'
      },
      {
        label: 'Option 2',
        value: 'option2'
      },
      {
        label: 'Option 3',
        value: 'option3'
      }
    ]}
  />
 *
 * @example
 * // Vertical Orientation
  <Radio
    options={[
      {
        label: 'Option 1',
        value: 'option1'
      },
      {
        label: 'Option 2',
        value: 'option2'
      },
      {
        label: 'Option 3',
        value: 'option3'
      }
    ]}
  />
 *
 * @example
 * // Disabled Radio Group
 * <Radio
 *   options={[
 *     {
 *       label: 'Option 1',
 *       value: 'option1'
 *     },
 *     {
 *       label: 'Option 2',
 *       value: 'option2'
 *     },
 *     {
 *       label: 'Option 3',
 *       value: 'option3'
 *     }
 *   ]}
 *   disabled
 * />
 */

export const Radio = React.forwardRef<
  React.ElementRef<typeof ShadcnRadio>,
  RadioProps
>(({ className, options, disabled, ...props }, ref) => {
  return (
    <ShadcnRadio ref={ref} className={className} disabled={disabled} {...props}>
      {options.map((option) => (
        <Flex key={option.value} align='center' gap='xxs' direction='row'>
          <ShadcnRadioItem
            id={option.value}
            value={option.value}
            disabled={option.disabled}
            aria-invalid={option.error}
          />
          <Label
            className={cn(
              option.error && 'text-error-500',
              (option.disabled || disabled) && 'opacity-50',
              'font-normal',
            )}
            htmlFor={option.value}
          >
            {option.label}
          </Label>
        </Flex>
      ))}
    </ShadcnRadio>
  );
});

Radio.displayName = 'Radio';
